import React, { useEffect, useState } from 'react';
import { useLazyQuery } from '@apollo/client';
import { getCancellationAmendmentDetails } from './queries';
import CancellationDetails from './components/CancellationDetails';
import PolicyPlanCoversListingWidget from './components/policy-plan-covers-listing/PolicyPlanCoversListingWidget';
import CancellationCostsAndCharges from './components/CancellationCostsAndCharges';
import Loader from '@components/loader/Loader';
import {
  enumListAsRecordObject,
  lookupListAsRecordObject,
} from '@utils/graph-utils';
import { IAmendmentGeneralData, ILineAmendmentLovs } from './line-amendment';
import { IAbstractRecord } from '@models';
import { IAmendmentPageState } from '.';
import { useAppSelector } from '@redux/hooks';
import InsuredDetailsListingWidget from './components/insured-details-listing/InsuredDetailsListingWidget';

export interface ICancellationDetailsWidgetProps {
  generalData: IAmendmentGeneralData;
  data: IAbstractRecord;
  pageState: IAmendmentPageState;
}

const CancellationDetailsWidget: React.FC<ICancellationDetailsWidgetProps> = ({
  generalData,
  data,
  pageState,
}) => {
  const user = useAppSelector((state) => state.user);

  const [loading, setLoading] = useState<boolean>(false);
  const [cancellationDetails, setCancellationDetails] = useState<any>(null);
  const [lovs, setLovs] = useState<ILineAmendmentLovs>({
    amendmentTypes: {},
    amendmentSubTypes: {},
    cancellationReasons: {},
  });

  const [getCancellationAmendmentDetailsLazy] = useLazyQuery(
    getCancellationAmendmentDetails,
    {
      variables: {
        selectedAmendmentID: generalData.amendment.id,
      },
      errorPolicy: 'all',
    }
  );

  const initializeCancellation = async () => {
    setLoading(true);
    const { data: cancellationAmendmentData } =
      await getCancellationAmendmentDetailsLazy();
    setCancellationDetails(
      cancellationAmendmentData?.Production?.queries
        ?.getCancellationAmendmentDetails
    );

    setLovs({
      amendmentTypes: enumListAsRecordObject(
        data.Production_AmendmentTypes.enumValues
      ),
      amendmentSubTypes: lookupListAsRecordObject(
        data.Production.lookups.amendmentSubTypes,
        false,
        'Code',
        'Title'
      ),
      cancellationReasons: enumListAsRecordObject(
        data?.Production_CancellationReasons?.enumValues
      ),
    });
    setLoading(false);
  };

  useEffect(() => {
    if (data) {
      initializeCancellation();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  if (loading || !cancellationDetails) {
    return <Loader />;
  }

  return (
    <>
      <CancellationDetails
        lovs={lovs as IAbstractRecord}
        onDataRefresh={initializeCancellation}
        data={cancellationDetails?.[0]}
        generalData={generalData}
        allowEndorsement={user.info.businessUser_AllowEndorsement}
      />
      {!['19', '48'].includes(generalData.line.externalCode) && (
        <PolicyPlanCoversListingWidget
          selectedAmendmentID={generalData.amendment.id}
          onDataRefresh={initializeCancellation}
          allowEndorsement={user.info.businessUser_AllowEndorsement}
          lineExternalCode={generalData.line.externalCode}
          status={generalData.amendment.status}
        />
      )}
      {generalData.line.externalCode === '19' && (
        <InsuredDetailsListingWidget
          selectedAmendmentID={generalData.amendment.id}
        />
      )}
      <CancellationCostsAndCharges
        data={cancellationDetails?.[0]}
        lineExternalCode={generalData.line.externalCode}
        onDataRefresh={initializeCancellation}
        generalData={generalData}
        allowEndorsement={user.info.businessUser_AllowEndorsement}
      />
    </>
  );
};

export default CancellationDetailsWidget;
